import { useClerk, useUser } from "@clerk/react";
import { Link } from "@tanstack/react-router";
import type { ReactElement } from "react";
import type { AlwaysAiUser } from "../../../../shared/api-types";
import {
  hasNotificationSettings,
  NOTIFICATION_SETTINGS_PATH,
} from "@/lib/notification-settings-client";
import { hasAdminSettings, whoRoleLine } from "@/lib/rail-sections";
import { NOTIFICATIONS_FLAG, useFeatureEnabled } from "@/lib/use-features";
import {
  RailAccountAvatarButton,
  RailAccountRow,
  type RailAccountMenuProps,
  type RenderAdminSettingsLink,
} from "./rail-account-parts";
import type { ShellLookSwitch } from "./shell-header";

// The rail shell's account menu with Clerk and the router wired in (NO-754).
// Shell chrome, so only the rail shell imports it (the guard in
// shell-layout.test.tsx); the classic shell keeps Clerk's UserButton.

const renderAdminLink: RenderAdminSettingsLink = ({ className, children }) => (
  <Link to="/admin/rds" className={className}>
    {children}
  </Link>
);

const renderNotificationSettingsLink: RenderAdminSettingsLink = ({
  className,
  children,
}) => (
  <Link to={NOTIFICATION_SETTINGS_PATH} className={className}>
    {children}
  </Link>
);

function useRailAccountMenu(
  user: AlwaysAiUser,
  lookSwitch: ShellLookSwitch | undefined
): RailAccountMenuProps {
  const { signOut } = useClerk();
  const { user: clerkUser } = useUser();
  const notificationsEnabled = useFeatureEnabled(NOTIFICATIONS_FLAG);

  return {
    account: {
      id: user.id,
      name: user.name,
      email: user.email,
      roleLine: whoRoleLine(user),
      imageUrl: clerkUser?.imageUrl,
      hasImage: clerkUser?.hasImage,
    },
    notificationSettings: hasNotificationSettings(user, notificationsEnabled),
    renderNotificationSettingsLink,
    adminSettings: hasAdminSettings(user),
    lookSwitch,
    // No redirect here: Clerk sends the viewer to the provider's
    // afterSignOutUrl, as its UserButton's Sign out does.
    onSignOut: () => void signOut(),
    renderAdminLink,
  };
}

/** The account row at the foot of the rail. */
export function RailAccountFoot({
  user,
  lookSwitch,
  collapsed,
  isDesktop,
}: {
  user: AlwaysAiUser;
  lookSwitch?: ShellLookSwitch;
  collapsed: boolean;
  isDesktop: boolean;
}): ReactElement {
  const menu = useRailAccountMenu(user, lookSwitch);

  return (
    <div className="sticky bottom-0 mt-2 border-t border-border bg-muted pt-2 brand:bg-background">
      <RailAccountRow
        menu={menu}
        collapsed={collapsed}
        opensRight={collapsed && isDesktop}
      />
    </div>
  );
}

/** The phone bar's avatar, opening the same menu. */
export function RailAccountBarButton({
  user,
  lookSwitch,
}: {
  user: AlwaysAiUser;
  lookSwitch?: ShellLookSwitch;
}): ReactElement {
  return <RailAccountAvatarButton menu={useRailAccountMenu(user, lookSwitch)} />;
}
